import { customersDropdownHook } from '@hooks/crm';
import { useEffect, useState } from 'react';
import { SearchSuggest } from '@skupreme/shared/ui';
import type {
  CustomerResult,
  CustomerSearchInputProps,
} from 'types/components/custom-search-input';

type CustomerRow = { id: string; customer: CustomerResult };

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The text with each case-insensitive match of `search` in bold. */
function Highlighted({ text, search }: { text: string; search?: string }) {
  if (!search) {
    return <>{text}</>;
  }
  const parts = text.split(new RegExp(`(${escapeRegExp(search)})`, 'gi'));
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional and never reorder
          <strong key={index}>{part}</strong>
        ) : (
          part
        ),
      )}
    </>
  );
}

const CELL = 'sk:min-w-0 sk:truncate';

function CustomerOption({ customer }: { customer: CustomerResult }) {
  const { address, email, name, phone, search } = customer;
  const {
    addressLine1,
    countryCode,
    districtOrCounty,
    postalCode,
    stateOrRegion,
  } = address || {};

  const showAddress = [
    addressLine1,
    postalCode,
    districtOrCounty,
    stateOrRegion,
    countryCode,
  ]
    .filter(d => !!d)
    .join(', ');

  return (
    <div className="sk:grid sk:w-full sk:gap-x-3 sk:md:grid-cols-[3fr_3fr_2fr_4fr]">
      <span className={`${CELL} sk:text-fg`} title={name}>
        <Highlighted text={name || ''} search={search} />
      </span>
      <span className={`${CELL} sk:text-fg-tertiary`} title={email}>
        <Highlighted text={email || ''} search={search} />
      </span>
      <span className={`${CELL} sk:text-fg-tertiary`} title={phone}>
        <Highlighted text={phone || ''} search={search} />
      </span>
      <span className={`${CELL} sk:text-fg-tertiary`} title={showAddress}>
        <Highlighted text={showAddress} search={search} />
      </span>
    </div>
  );
}

export function CustomerSearchInput({
  top,
  onResultClick,
}: CustomerSearchInputProps) {
  const [data, setData] = useState<CustomerResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const { data: hookData, loading: hookLoading } =
    customersDropdownHook(search);

  useEffect(() => {
    setData([]);
    setLoading(!!search);
  }, [search]);

  useEffect(() => {
    setData((hookData || []) as CustomerResult[]);
  }, [hookData]);

  useEffect(() => {
    setLoading(!!hookLoading);
  }, [hookLoading]);

  const rows: CustomerRow[] = data
    .filter(customer => !!customer?._id)
    .map(customer => ({ id: String(customer._id), customer }));

  return (
    <SearchSuggest
      label="Search existing customers"
      placeholder="Name, Customer ID, E-Mail Address"
      value={search}
      onValueChange={setSearch}
      items={rows}
      onSelect={row => {
        onResultClick(row.customer);
        setSearch('');
      }}
      isLoading={loading}
      emptyText="No results found."
      placement={top ? 'top' : 'bottom'}
      openOnFocus
      isFluid
    >
      {row => <CustomerOption customer={row.customer} />}
    </SearchSuggest>
  );
}
